import React, { useEffect, useState } from 'react';
import { ArmoryLoadout, ArmoryItem, Gear, KratosTable, Mnemos, Titan, Weapon, ArmoryItemType } from '../../core/types';
import { Constants } from '../../core/constants';
import { Utils } from '../../core/utilities';
import { useConfirm } from '../../hooks/useConfirm';
import { ConfirmModal } from '../../components/ConfirmModal';
import SearchableSelect from '../../components/SearchableSelect';
import {
    ArmoryCollectionKey,
    createCustomDuplicate,
    deleteCustomArmoryItem,
    isBundledArmoryItem,
    rebindArmoryLoadouts,
    upsertCustomArmoryItem,
} from '../../core/utilities/armory-storage';
import Button from '../../components/Button';

interface ArmorySaveSectionInputProps {
    editingItem: ArmoryItem;
    titans: Titan[];
    kratosTables: KratosTable[];
    weapons: Weapon[];
    gear: Gear[];
    mnemos: Mnemos[];
    visibleTags: string[];
    loadouts: ArmoryLoadout[];
    defaultLoadout: ArmoryLoadout;
    setEditingItem: React.Dispatch<React.SetStateAction<ArmoryItem>>;
    setTitans: React.Dispatch<React.SetStateAction<Titan[]>>;
    setKratosTables: React.Dispatch<React.SetStateAction<KratosTable[]>>;
    setWeapons: React.Dispatch<React.SetStateAction<Weapon[]>>;
    setGear: React.Dispatch<React.SetStateAction<Gear[]>>;
    setMnemos: React.Dispatch<React.SetStateAction<Mnemos[]>>;
    setVisibleTags: React.Dispatch<React.SetStateAction<string[]>>;
    setLoadouts: React.Dispatch<React.SetStateAction<ArmoryLoadout[]>>;
}

export const ArmorySaveSection: React.FC<ArmorySaveSectionInputProps> = ({
    editingItem,
    titans,
    kratosTables,
    weapons,
    gear,
    mnemos,
    visibleTags,
    loadouts,
    defaultLoadout,
    setEditingItem,
    setTitans,
    setKratosTables,
    setWeapons,
    setGear,
    setMnemos,
    setVisibleTags,
    setLoadouts,
}) => {
    const [selectedItemType, setSelectedItemType] = useState<ArmoryItemType | null>(null);
    const [selectedItem, setSelectedItem] = useState<ArmoryItem | null>(null);
    const [inputTag, setInputTag] = useState('');
    const [validationMessage, setValidationMessage] = useState('');
    const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm();

    const currentArmory = { titans, kratosTables, weapons, gear, mnemos };
    const selectedItemIsBundled = isBundledArmoryItem(selectedItem);
    const isUpdatingExistingItem = Boolean(selectedItem && !selectedItemIsBundled);

    const getAllItemsForItemType = (itemType: ArmoryItemType | null) => {
        let items: ArmoryItem[];
        switch (itemType) {
            case ArmoryItemType.TITAN:
                items = titans;
                break;
            case ArmoryItemType.KRATOS_TABLE:
                items = kratosTables;
                break;
            case ArmoryItemType.WEAPON:
                items = weapons;
                break;
            case ArmoryItemType.GEAR:
                items = gear;
                break;
            case ArmoryItemType.MNEMOS:
                items = mnemos;
                break;
            default:
                items = [];
        }
        return items;
    };

    const getItemsForItemType = (itemType: ArmoryItemType | null) => getAllItemsForItemType(itemType).filter((item) => Utils.shouldShowTaggedEntity(item, visibleTags));

    const getTags = () => {
        return Utils.getUniqueTags([...titans, ...kratosTables, ...weapons, ...gear, ...mnemos, ...Object.values(Constants.SpoilerTag).map((t) => ({ tags: [t] }))]);
    };

    const getCollectionKey = (itemType: ArmoryItemType): ArmoryCollectionKey => {
        switch (itemType) {
            case ArmoryItemType.TITAN:
                return 'titans';
            case ArmoryItemType.KRATOS_TABLE:
                return 'kratosTables';
            case ArmoryItemType.WEAPON:
                return 'weapons';
            case ArmoryItemType.GEAR:
                return 'gear';
            case ArmoryItemType.MNEMOS:
            default:
                return 'mnemos';
        }
    };

    const handleItemTypeChange = (itemType: ArmoryItemType) => {
        setSelectedItemType(itemType);
        setSelectedItem(null);
        setValidationMessage('');
    };

    const handleItemSelect = (item: ArmoryItem) => {
        setSelectedItem(item);
        const sanitizedItem: ArmoryItem = {
            id: item.id,
            name: item.name ?? '',
            source: item.source,
            bundledOriginId: item.bundledOriginId,
            bundledOriginName: item.bundledOriginName,
            tags: item.tags ?? [],
            stats: item.stats.map((stats) => Utils.sanitizeLoadoutStatsWithRequirements(Utils.deepCopy(stats))),
        };
        setEditingItem(sanitizedItem);
        setValidationMessage('');
    };

    const handleNameChange = (newName: string) => {
        setEditingItem((prev) => ({ ...prev, name: newName }));
        setValidationMessage('');
    };

    const setCollectionItems = (itemType: ArmoryItemType, nextItems: ArmoryItem[]) => {
        const nextArmory = {
            ...currentArmory,
            [getCollectionKey(itemType)]: nextItems,
        };

        switch (itemType) {
            case ArmoryItemType.TITAN:
                setTitans(nextItems as Titan[]);
                break;
            case ArmoryItemType.KRATOS_TABLE:
                setKratosTables(nextItems as KratosTable[]);
                break;
            case ArmoryItemType.WEAPON:
                setWeapons(nextItems as Weapon[]);
                break;
            case ArmoryItemType.GEAR:
                setGear(nextItems as Gear[]);
                break;
            case ArmoryItemType.MNEMOS:
                setMnemos(nextItems as Mnemos[]);
                break;
        }

        const allTags = Utils.getUniqueTags([
            ...nextArmory.titans,
            ...nextArmory.kratosTables,
            ...nextArmory.weapons,
            ...nextArmory.gear,
            ...nextArmory.mnemos,
            ...Object.values(Constants.SpoilerTag).map((tag) => ({ tags: [tag] })),
        ]);
        setVisibleTags(visibleTags.filter((tag) => allTags.includes(tag)));
        setLoadouts(rebindArmoryLoadouts(loadouts, nextArmory, defaultLoadout));
    };

    const validateEditableName = (itemType: ArmoryItemType): string | null => {
        const desiredName = editingItem.name.trim();
        const items = getAllItemsForItemType(itemType);
        const bundledNameMatch = items.find((item) => item.name === desiredName && isBundledArmoryItem(item));
        const customNameMatch = items.find((item) => item.name === desiredName && !isBundledArmoryItem(item));

        if (!desiredName) {
            return 'Name is required.';
        }

        if (bundledNameMatch && (!selectedItem || bundledNameMatch.id !== selectedItem.id)) {
            return 'Bundled item names are reserved. Duplicate the bundled item first or choose a new custom name.';
        }

        if (customNameMatch && (!selectedItem || customNameMatch.id !== selectedItem.id)) {
            return `A custom ${itemType.toLowerCase()} named "${desiredName}" already exists.`;
        }

        return null;
    };

    const handleSave = async () => {
        if (!selectedItemType) return;
        const collectionKey = getCollectionKey(selectedItemType);
        const existingItems = getAllItemsForItemType(selectedItemType);
        const desiredName = editingItem.name.trim();

        if (!selectedItemIsBundled) {
            const nextValidationMessage = validateEditableName(selectedItemType);
            if (nextValidationMessage) {
                setValidationMessage(nextValidationMessage);
                return;
            }
        }

        if (isUpdatingExistingItem) {
            const confirmUpdate = await confirm(`You're about to ⚠️ UPDATE ⚠️ ${selectedItemType} "${selectedItem?.name}".  \n\n Do you want to continue?`);
            if (!confirmUpdate) return;
        }

        const draftItem = Utils.shallowCopy(editingItem);
        let duplicatedItem: ArmoryItem | null = null;
        let nextItems: ArmoryItem[];

        if (selectedItemIsBundled) {
            const customNameConflict = existingItems.find((item) => !isBundledArmoryItem(item) && item.name === desiredName);
            const isReservedBundledName = existingItems.some((item) => isBundledArmoryItem(item) && item.name === desiredName);

            if (desiredName && customNameConflict) {
                setValidationMessage(`A custom ${selectedItemType.toLowerCase()} named "${desiredName}" already exists.`);
                return;
            }

            const baseCustomItem = {
                ...draftItem,
                bundledOriginId: selectedItem?.id,
                bundledOriginName: selectedItem?.name,
            };
            duplicatedItem =
                desiredName && !isReservedBundledName
                    ? ({
                          ...baseCustomItem,
                          name: desiredName,
                      } as ArmoryItem)
                    : createCustomDuplicate(collectionKey, existingItems, {
                          ...baseCustomItem,
                          name: selectedItem?.name ?? editingItem.name,
                      });
            nextItems = upsertCustomArmoryItem(collectionKey, existingItems, duplicatedItem);
        } else {
            nextItems = upsertCustomArmoryItem(collectionKey, existingItems, draftItem, selectedItem);
        }
        setCollectionItems(selectedItemType, nextItems);
        const duplicatedItemId = duplicatedItem?.id;
        const savedItem =
            (duplicatedItemId
                ? nextItems.find((item) => item.id === duplicatedItemId)
                : nextItems.find((item) => item.id === draftItem.id) ?? nextItems.find((item) => item.name === draftItem.name && !isBundledArmoryItem(item))) ??
            editingItem;
        setSelectedItem(savedItem);
        setEditingItem(savedItem);
        setValidationMessage('');
    };

    const handleDelete = async () => {
        if (!selectedItemType || !selectedItem || selectedItemIsBundled) return;

        const confirmDelete = await confirm(`You're about to ❌ DELETE ❌ ${selectedItemType} "${selectedItem?.name}".  \n\nDo you want to continue?`);
        if (!confirmDelete) return;

        const nextItems = deleteCustomArmoryItem(getAllItemsForItemType(selectedItemType), selectedItem);
        setCollectionItems(selectedItemType, nextItems);

        setEditingItem(Constants.DEFAULT_EDITING_ITEM);
        setSelectedItem(null);
        setValidationMessage('');
    };

    const handleAddTag = (tag: string) => {
        setEditingItem((prev) => ({ ...prev, tags: Utils.getUniqueTags([prev, { tags: [tag] }]) }));
    };

    const handleRemoveTag = (tag: string) => {
        setEditingItem((prev) => ({ ...prev, tags: prev.tags.filter((itemTags) => itemTags !== tag) ?? [] }));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputTag(e.target.value);
    };

    const handleAddTagFromInput = () => {
        if (inputTag.trim()) {
            handleAddTag(inputTag.trim());
            setInputTag('');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleAddTagFromInput();
        }
    };

    useEffect(() => {
        if (selectedItem === null) {
            setEditingItem(Constants.DEFAULT_EDITING_ITEM);
        }
    }, [selectedItem, setEditingItem]);

    return (
        <div className="input-section armory-save-section">
            <div className="panel-section-header">
                <div className="panel-section-heading">
                    <h3>Custom Item</h3>
                </div>
            </div>
            <div className="loadout-select-row">
                <label className="loadout-select-label">Item Type</label>
                <div className="loadout-input-group">
                    <SearchableSelect
                        value={selectedItemType || ''}
                        options={[
                            { value: ArmoryItemType.TITAN, label: 'Titan' },
                            { value: ArmoryItemType.KRATOS_TABLE, label: 'Kratos Table' },
                            { value: ArmoryItemType.WEAPON, label: 'Weapon' },
                            { value: ArmoryItemType.GEAR, label: 'Gear' },
                            { value: ArmoryItemType.MNEMOS, label: 'Mnemos' },
                        ]}
                        onChange={(value) => handleItemTypeChange(value as ArmoryItemType)}
                        placeholder="(Select Item Type)"
                        ariaLabel="Item Type"
                    />
                </div>
            </div>
            <div className="loadout-select-row">
                <label className="loadout-select-label">Edit Existing</label>
                <div className="loadout-input-group">
                    <SearchableSelect
                        value={selectedItem?.name ?? ''}
                        options={getItemsForItemType(selectedItemType).map((item) => ({ value: item.name }))}
                        onChange={(value) => {
                            if (!value) {
                                setSelectedItem(null);
                                setValidationMessage('');
                                return;
                            }
                            const nextItem = getItemsForItemType(selectedItemType).find((item) => item.name === value);
                            if (nextItem) {
                                handleItemSelect(nextItem);
                            }
                        }}
                        placeholder={selectedItemType === null ? '' : '(None — creating new item)'}
                        ariaLabel="Edit Existing Item"
                        disabled={selectedItemType === null}
                        allowEmpty
                    />
                </div>
            </div>
            <div className="loadout-select-row">
                <label className="loadout-select-label">Name</label>
                <div className="loadout-input-group">
                    <input type="text" value={editingItem.name} onChange={(e) => handleNameChange(e.target.value)} placeholder={'Enter Name'} disabled={selectedItemType === null} />
                </div>
            </div>
            {selectedItemIsBundled && <p className="loadout-card-context">Bundled items are read-only. Save will create a custom duplicate instead of changing the bundled record.</p>}
            {validationMessage && <p className="loadout-card-context">{validationMessage}</p>}
            <div className="loadout-select-row armory-tag-row">
                <label className="loadout-select-label">Spoiler Tags</label>
                <div className="loadout-input-group">
                    <input type="text" list="tags-list" value={inputTag} onChange={handleInputChange} onKeyDown={handleKeyPress} placeholder="Enter Spoiler Tag" disabled={selectedItemType === null} />
                    <datalist id="tags-list">
                        {getTags().map((tag) => (
                            <option key={tag} value={tag} />
                        ))}
                    </datalist>
                </div>
                <div className="increment-buttons armory-tag-actions">
                    <Button type="button" className="armory-tag-add-button" variant="secondary" size="sm" onClick={handleAddTagFromInput}>
                        +
                    </Button>
                </div>
            </div>
            <div className="loadout-row">
                <ul className="selected-items">
                    {editingItem.tags.sort().map((tag) => (
                        <li key={tag}>
                            <Button type="button" className="remove-item" variant="ghost" size="sm" aria-label={`Remove ${tag}`} onClick={() => handleRemoveTag(tag)}>
                                x
                            </Button>
                            {tag}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="spacer" />
            <div className="input-section-button-container">
                <Button variant="primary" fullWidth onClick={handleSave} disabled={selectedItemType === null || editingItem?.name === ''}>
                    {selectedItemIsBundled
                        ? `Duplicate to Custom ${selectedItemType}`
                        : isUpdatingExistingItem
                          ? `Update ${selectedItemType}`
                          : `Save New ${selectedItemType ?? 'Item'}`}
                </Button>
                <Button variant="danger" fullWidth onClick={handleDelete} disabled={!isUpdatingExistingItem}>
                    Delete {selectedItemType ?? 'Item'}
                </Button>
                <Button className="loadout-action-button-wide" variant="danger" fullWidth onClick={() => handleItemTypeChange(null!)}>
                    Reset Selection
                </Button>
            </div>
            {confirmState && <ConfirmModal message={confirmState.message} onConfirm={handleConfirm} onCancel={handleCancel} />}
        </div>
    );
};
